import { useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ChevronDown, ChevronUp, GripVertical } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Skeleton } from "../ui/skeleton";
import { ChartContainer } from "../ChartContainer";
import { ChartCardHeader } from "../ChartCardHeader";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Button } from "../ui/button";
import { AreaChart, Area, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, ReferenceLine } from "recharts";
import { formatCurrency, stringToColor } from "../../lib/utils";

const COLORS = ["#10b981", "#8b5cf6", "#f59e0b", "#ec4899", "#0ea5e9", "#6366f1"];
const RADIAN = Math.PI / 180;
const STORAGE_KEY = "kawa.dashboard.chartOrder";
const DEFAULT_ORDER = ["cash-flow", "annual-flow", "expense-categories", "income-categories"];
const ANNUAL_MONTH_LABELS = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

function formatPercent(value) {
  return new Intl.NumberFormat("pt-BR", {
    style: "percent",
    minimumFractionDigits: value < 0.1 ? 1 : 0,
    maximumFractionDigits: value < 0.1 ? 1 : 0,
  }).format(value || 0);
}

function withPercentages(data) {
  const total = data.reduce((sum, item) => sum + (Number(item.value) || 0), 0);
  return data.map((item, index) => {
    const percent = total > 0 ? (Number(item.value) || 0) / total : 0;
    return {
      ...item,
      color: item.color || COLORS[index % COLORS.length] || stringToColor(item.name),
      percent,
      percentLabel: formatPercent(percent),
    };
  });
}

function maskValue(value, privacyMode) {
  return privacyMode ? "••••" : formatCurrency(value);
}

function formatAxisCurrency(value, privacyMode) {
  if (privacyMode) return "••";
  const absValue = Math.abs(Number(value) || 0);
  const sign = Number(value) < 0 ? "-" : "";
  if (absValue >= 1000) return `${sign}R$ ${(absValue / 1000).toFixed(1)}k`;
  return `${sign}R$ ${absValue.toFixed(0)}`;
}

function formatCompactNumber(value, privacyMode) {
  if (privacyMode) return "••";
  const numericValue = Number(value) || 0;
  const absValue = Math.abs(numericValue);
  const sign = numericValue < 0 ? "-" : "";
  if (absValue >= 1000) return `${sign}${(absValue / 1000).toFixed(1)}k`;
  return `${sign}${absValue.toFixed(0)}`;
}

function formatPlainCurrencyValue(value, privacyMode) {
  if (privacyMode) return "••••";
  return new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);
}

function AnnualMonthTick({ x, y, payload }) {
  const monthIndex = ANNUAL_MONTH_LABELS.indexOf(payload?.value);
  const offset = monthIndex >= 0 && monthIndex % 2 === 1 ? 24 : 10;

  return (
    <text
      x={x}
      y={y}
      dy={offset}
      fill="currentColor"
      opacity={0.6}
      textAnchor="middle"
      fontSize={12}
    >
      {payload?.value}
    </text>
  );
}

function renderOutsidePercentLabel({ cx, cy, midAngle, outerRadius, percent }) {
  if (!percent || percent < 0.005) return null;

  const radius = outerRadius + 16;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="hsl(var(--foreground))"
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
      fontSize={11}
      fontWeight={700}
    >
      {formatPercent(percent)}
    </text>
  );
}

function CashFlowTooltip({ active, payload, label, privacyMode }) {
  if (!active || !payload?.length) return null;

  const item = payload[0].payload;
  return (
    <div className="rounded-md border bg-card px-3 py-2 text-sm shadow-sm">
      <p className="font-medium">Dia {label}</p>
      <p className="font-mono text-foreground">Saldo: {maskValue(item.balance, privacyMode)}</p>
      <p className="text-xs text-success">Receitas: {maskValue(item.income, privacyMode)}</p>
      <p className="text-xs text-destructive">Despesas: {maskValue(item.expense, privacyMode)}</p>
    </div>
  );
}

function CategoryTooltip({ active, payload, privacyMode }) {
  if (!active || !payload?.length) return null;

  const item = payload[0].payload;
  return (
    <div className="rounded-md border bg-card px-3 py-2 text-sm shadow-sm">
      <p className="font-medium">{item.name}</p>
      <p className="font-mono text-foreground">{maskValue(item.value, privacyMode)}</p>
      <p className="text-xs text-muted-foreground">{item.percentLabel} do total</p>
    </div>
  );
}

function AnnualFlowTooltip({ active, payload, label, privacyMode }) {
  if (!active || !payload?.length) return null;

  const income = payload.find((item) => item.dataKey === "income")?.value || 0;
  const expense = payload.find((item) => item.dataKey === "expense")?.value || 0;

  return (
    <div className="rounded-md border bg-card px-3 py-2 text-sm shadow-sm">
      <p className="font-medium">{label}</p>
      <p className="text-xs text-success">Receitas: {formatPlainCurrencyValue(income, privacyMode)}</p>
      <p className="text-xs text-destructive">Despesas: {formatPlainCurrencyValue(expense, privacyMode)}</p>
    </div>
  );
}

function CategoryLegend({ data, privacyMode }) {
  return (
    <div className="grid gap-2 text-xs sm:grid-cols-2">
      {data.slice(0, 6).map((entry) => (
        <div key={entry.name} className="flex min-w-0 items-center justify-between gap-2 rounded-md bg-muted/40 px-2 py-1.5">
          <span className="flex min-w-0 items-center gap-2">
            <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: entry.color }} />
            <span className="truncate text-muted-foreground">{entry.name}</span>
          </span>
          <span className="shrink-0 text-right font-mono font-semibold text-foreground">
            {entry.percentLabel}
            <span className={privacyMode ? "privacy-blur ml-1 inline-block" : "ml-1 inline-block"}>
              {maskValue(entry.value, privacyMode)}
            </span>
          </span>
        </div>
      ))}
    </div>
  );
}

function LegendToggle({ visible, onToggle }) {
  return (
    <div className="flex items-center gap-2">
      <Label className="text-sm font-normal text-muted-foreground">Legenda</Label>
      <Switch checked={visible} onCheckedChange={onToggle} />
    </div>
  );
}

function ReorderControls({ onMoveUp, onMoveDown, canMoveUp, canMoveDown, dragHandleProps }) {
  return (
    <div className="flex items-center gap-1" onClick={(event) => event.stopPropagation()}>
      <button
        type="button"
        className="hidden cursor-grab rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground lg:inline-flex"
        title="Arrastar gráfico"
        aria-label="Arrastar gráfico"
        {...dragHandleProps}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onMoveUp} disabled={!canMoveUp} title="Mover para cima">
        <ArrowUp className="h-4 w-4" />
      </Button>
      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onMoveDown} disabled={!canMoveDown} title="Mover para baixo">
        <ArrowDown className="h-4 w-4" />
      </Button>
    </div>
  );
}

function normalizeOrder(order) {
  if (!Array.isArray(order)) return DEFAULT_ORDER;

  const known = order.filter((item) => DEFAULT_ORDER.includes(item));
  const missing = DEFAULT_ORDER.filter((item) => !known.includes(item));
  return [...known, ...missing];
}

export function DashboardChartsRow({
  cashFlowData,
  annualCashFlowData,
  categoryDataExpense,
  categoryDataIncome,
  privacyMode,
  isLoading,
}) {
  const [showExpenseLegend, setShowExpenseLegend] = useState(true);
  const [showIncomeLegend, setShowIncomeLegend] = useState(true);
  const [showAnnualDetails, setShowAnnualDetails] = useState(false);
  const [draggedChart, setDraggedChart] = useState(null);
  const [chartOrder, setChartOrder] = useState(() => {
    try {
      return normalizeOrder(JSON.parse(localStorage.getItem(STORAGE_KEY)));
    } catch (_) {
      return DEFAULT_ORDER;
    }
  });

  const expenseWithPercent = useMemo(() => withPercentages(categoryDataExpense), [categoryDataExpense]);
  const incomeWithPercent = useMemo(() => withPercentages(categoryDataIncome), [categoryDataIncome]);
  const cashFlowTotal = cashFlowData?.[cashFlowData.length - 1]?.balance || 0;
  const hasCashFlowActivity = cashFlowData?.some((item) => item.income || item.expense);
  const annualTotals = useMemo(() => {
    return (annualCashFlowData || []).reduce((totals, item) => ({
      income: totals.income + (Number(item.income) || 0),
      expense: totals.expense + (Number(item.expense) || 0),
    }), { income: 0, expense: 0 });
  }, [annualCashFlowData]);
  const annualAverages = useMemo(() => ({
    income: annualTotals.income / 12,
    expense: annualTotals.expense / 12,
  }), [annualTotals]);
  const hasAnnualActivity = annualCashFlowData?.some((item) => item.income || item.expense);
  const AnnualDetailsIcon = showAnnualDetails ? ChevronUp : ChevronDown;

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(chartOrder));
  }, [chartOrder]);

  const moveChart = (chartId, direction) => {
    setChartOrder((current) => {
      const index = current.indexOf(chartId);
      const nextIndex = index + direction;
      if (index < 0 || nextIndex < 0 || nextIndex >= current.length) return current;

      const next = [...current];
      [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
      return next;
    });
  };

  const handleDrop = (targetChart) => {
    if (!draggedChart || draggedChart === targetChart) return;

    setChartOrder((current) => {
      const next = current.filter((item) => item !== draggedChart);
      const targetIndex = next.indexOf(targetChart);
      next.splice(targetIndex, 0, draggedChart);
      return next;
    });
    setDraggedChart(null);
  };

  const makeControls = (chartId) => {
    const index = chartOrder.indexOf(chartId);
    return (
      <ReorderControls
        canMoveUp={index > 0}
        canMoveDown={index >= 0 && index < chartOrder.length - 1}
        onMoveUp={() => moveChart(chartId, -1)}
        onMoveDown={() => moveChart(chartId, 1)}
        dragHandleProps={{
          draggable: true,
          onDragStart: () => setDraggedChart(chartId),
          onDragEnd: () => setDraggedChart(null),
        }}
      />
    );
  };

  const cards = {
    "cash-flow": (
      <Card className="card-hover lg:col-span-2" key="cash-flow">
        <ChartCardHeader
          title="Fluxo de Caixa"
          actions={makeControls("cash-flow")}
        />
        <CardContent>
          {isLoading ? <Skeleton className="h-72 w-full" /> : cashFlowData.length > 0 ? (
            <div className="space-y-4">
              <ChartContainer className="h-72">
                <AreaChart data={cashFlowData} margin={{ top: 12, right: 12, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="cashFlowGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ec4899" stopOpacity={0.45} />
                      <stop offset="95%" stopColor="#ec4899" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="day"
                    tickFormatter={(value) => String(value)}
                    stroke="currentColor"
                    strokeOpacity={0.3}
                    tick={{ fill: "currentColor", opacity: 0.6 }}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    stroke="currentColor"
                    strokeOpacity={0.3}
                    tick={{ fill: "currentColor", opacity: 0.6 }}
                    tickFormatter={(value) => formatAxisCurrency(value, privacyMode)}
                    width={72}
                  />
                  <ReferenceLine y={0} stroke="currentColor" strokeOpacity={0.2} />
                  <Tooltip content={<CashFlowTooltip privacyMode={privacyMode} />} />
                  <Area
                    type="monotone"
                    dataKey="balance"
                    stroke="#ec4899"
                    strokeWidth={3}
                    fillOpacity={1}
                    fill="url(#cashFlowGradient)"
                    name="Saldo"
                  />
                </AreaChart>
              </ChartContainer>
              <div className="flex items-center justify-between gap-3 border-t pt-3">
                <span className="text-lg font-semibold">Total</span>
                <span className={`font-mono text-xl font-bold ${cashFlowTotal < 0 ? "text-destructive" : "text-success"} ${privacyMode ? "privacy-blur" : ""}`}>
                  {maskValue(cashFlowTotal, privacyMode)}
                </span>
              </div>
              {!hasCashFlowActivity && (
                <p className="text-center text-sm text-muted-foreground">Sem receitas ou despesas no mês selecionado</p>
              )}
            </div>
          ) : (
            <div className="flex h-72 items-center justify-center text-muted-foreground"><p>Adicione transações para ver o gráfico</p></div>
          )}
        </CardContent>
      </Card>
    ),
    "annual-flow": (
      <Card className="card-hover lg:col-span-2" key="annual-flow">
        <ChartCardHeader
          title="Fluxo Anual"
          actions={
            <>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => setShowAnnualDetails((value) => !value)}
                title={showAnnualDetails ? "Recolher detalhes mensais" : "Expandir detalhes mensais"}
                aria-label={showAnnualDetails ? "Recolher detalhes mensais" : "Expandir detalhes mensais"}
              >
                <AnnualDetailsIcon className="h-4 w-4" />
              </Button>
              {makeControls("annual-flow")}
            </>
          }
        />
        <CardContent>
          {isLoading ? <Skeleton className="h-72 w-full" /> : annualCashFlowData?.length > 0 ? (
            <div className="space-y-4">
              <ChartContainer className="h-72">
                <AreaChart data={annualCashFlowData} margin={{ top: 12, right: 12, left: 0, bottom: 20 }}>
                  <defs>
                    <linearGradient id="annualIncomeGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.32} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0.03} />
                    </linearGradient>
                    <linearGradient id="annualExpenseGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ef4444" stopOpacity={0.28} />
                      <stop offset="95%" stopColor="#ef4444" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="month"
                    stroke="currentColor"
                    strokeOpacity={0.3}
                    tick={<AnnualMonthTick />}
                    tickLine={false}
                    height={46}
                    interval={0}
                  />
                  <YAxis
                    stroke="currentColor"
                    strokeOpacity={0.3}
                    tick={{ fill: "currentColor", opacity: 0.6 }}
                    tickFormatter={(value) => formatCompactNumber(value, privacyMode)}
                    width={52}
                  />
                  <Tooltip content={<AnnualFlowTooltip privacyMode={privacyMode} />} />
                  <Area
                    type="monotone"
                    dataKey="income"
                    stroke="#10b981"
                    strokeWidth={3}
                    fill="url(#annualIncomeGradient)"
                    fillOpacity={1}
                    dot={{ r: 3, strokeWidth: 2, fill: "#10b981" }}
                    activeDot={{ r: 5 }}
                    name="Receitas"
                  />
                  <Area
                    type="monotone"
                    dataKey="expense"
                    stroke="#ef4444"
                    strokeWidth={3}
                    fill="url(#annualExpenseGradient)"
                    fillOpacity={1}
                    dot={{ r: 3, strokeWidth: 2, fill: "#ef4444" }}
                    activeDot={{ r: 5 }}
                    name="Despesas"
                  />
                </AreaChart>
              </ChartContainer>
              <div className="grid gap-2 border-t pt-3 sm:grid-cols-2">
                <div className="flex items-center justify-between gap-3 rounded-md bg-muted/40 px-3 py-2">
                  <span className="text-sm font-medium text-muted-foreground">Média Receitas</span>
                  <span className={`font-mono font-bold text-success ${privacyMode ? "privacy-blur" : ""}`}>
                    {formatPlainCurrencyValue(annualAverages.income, privacyMode)}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3 rounded-md bg-muted/40 px-3 py-2">
                  <span className="text-sm font-medium text-muted-foreground">Média Despesas</span>
                  <span className={`font-mono font-bold text-destructive ${privacyMode ? "privacy-blur" : ""}`}>
                    {formatPlainCurrencyValue(annualAverages.expense, privacyMode)}
                  </span>
                </div>
              </div>
              {showAnnualDetails && (
                <div className="overflow-x-auto rounded-md border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold">Mês</th>
                        <th className="px-3 py-2 text-right font-semibold">Receitas</th>
                        <th className="px-3 py-2 text-right font-semibold">Despesas</th>
                      </tr>
                    </thead>
                    <tbody>
                      {annualCashFlowData.map((item) => (
                        <tr key={item.month} className="border-t">
                          <td className="px-3 py-2 font-medium">{item.month}</td>
                          <td className="px-3 py-2 text-right font-mono font-semibold text-success">
                            {formatPlainCurrencyValue(item.income, privacyMode)}
                          </td>
                          <td className="px-3 py-2 text-right font-mono font-semibold text-destructive">
                            {formatPlainCurrencyValue(item.expense, privacyMode)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {!hasAnnualActivity && (
                <p className="text-center text-sm text-muted-foreground">Sem receitas ou despesas no ano selecionado</p>
              )}
            </div>
          ) : (
            <div className="flex h-72 items-center justify-center text-muted-foreground"><p>Adicione transações para ver o gráfico</p></div>
          )}
        </CardContent>
      </Card>
    ),
    "expense-categories": (
      <Card className="card-hover" key="expense-categories">
        <ChartCardHeader
          title="Despesas por Categoria"
          actions={
            <>
              <LegendToggle visible={showExpenseLegend} onToggle={setShowExpenseLegend} />
              {makeControls("expense-categories")}
            </>
          }
        />
        <CardContent>
          {isLoading ? <Skeleton className="h-64 w-full" /> : expenseWithPercent.length > 0 ? (
            <div className="space-y-4">
              <ChartContainer className="h-52 min-w-0">
                <PieChart>
                  <Pie
                    data={expenseWithPercent}
                    cx="50%"
                    cy="50%"
                    innerRadius={58}
                    outerRadius={78}
                    paddingAngle={5}
                    dataKey="value"
                    label={renderOutsidePercentLabel}
                    labelLine={false}
                  >
                    {expenseWithPercent.map((entry, index) => <Cell key={`expense-cell-${index}`} fill={entry.color} />)}
                  </Pie>
                  <Tooltip content={<CategoryTooltip privacyMode={privacyMode} />} />
                </PieChart>
              </ChartContainer>
              {showExpenseLegend && <CategoryLegend data={expenseWithPercent} privacyMode={privacyMode} />}
            </div>
          ) : <div className="flex h-64 items-center justify-center text-muted-foreground"><p>Adicione despesas para ver o gráfico</p></div>}
        </CardContent>
      </Card>
    ),
    "income-categories": (
      <Card className="card-hover" key="income-categories">
        <ChartCardHeader
          title="Receitas por Categoria"
          actions={
            <>
              <LegendToggle visible={showIncomeLegend} onToggle={setShowIncomeLegend} />
              {makeControls("income-categories")}
            </>
          }
        />
        <CardContent>
          {isLoading ? <Skeleton className="h-64 w-full" /> : incomeWithPercent.length > 0 ? (
            <div className="space-y-4">
              <ChartContainer className="h-52 min-w-0">
                <PieChart>
                  <Pie
                    data={incomeWithPercent}
                    cx="50%"
                    cy="50%"
                    innerRadius={58}
                    outerRadius={78}
                    paddingAngle={5}
                    dataKey="value"
                    label={renderOutsidePercentLabel}
                    labelLine={false}
                  >
                    {incomeWithPercent.map((entry, index) => <Cell key={`income-cell-${index}`} fill={entry.color} />)}
                  </Pie>
                  <Tooltip content={<CategoryTooltip privacyMode={privacyMode} />} />
                </PieChart>
              </ChartContainer>
              {showIncomeLegend && <CategoryLegend data={incomeWithPercent} privacyMode={privacyMode} />}
            </div>
          ) : <div className="flex h-64 items-center justify-center text-muted-foreground"><p>Adicione receitas para ver o gráfico</p></div>}
        </CardContent>
      </Card>
    ),
  };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      {chartOrder.map((chartId) => (
        <div
          key={chartId}
          className={chartId === "cash-flow" || chartId === "annual-flow" ? "lg:col-span-2" : ""}
          onDragOver={(event) => event.preventDefault()}
          onDrop={() => handleDrop(chartId)}
        >
          {cards[chartId]}
        </div>
      ))}
    </div>
  );
}
